// ============================================================
// dev 環境識別 UI（O-8 / docs/design_dev_environment_indicator.md）
//
// dev では上部にアンバーのバナーを表示し、document title に [DEV] prefix、
// favicon を dev 版へ差し替える。本番では一切描画・変更しない。
//
// 広報スクショ用に、バナーを「ダブルタップ / ダブルクリック」で一時的に
// 隠せる。隠した状態は画面遷移 / 一定時間で自動復活する（隠したまま忘れて
// dev/prod を取り違えるのを防ぐ）。title・favicon は隠している間も維持する。
// ============================================================

import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { IS_DEV_ENV } from '@/lib/documentTitle'

const DOUBLE_TAP_MS = 350      // この間隔内の2回タップ/クリックで「隠す」
const AUTO_RESTORE_MS = 60000  // 隠したまま忘れ防止の保険（自動復活）

export default function EnvironmentIndicator() {
  // 本番では何も描画しない（prod build では VITE_APP_ENV!=='dev' で tree-shake 除去）
  if (!IS_DEV_ENV) return null
  return <DevBanner />
}

function DevBanner() {
  const location = useLocation()
  const [hidden, setHidden] = useState(false)
  const lastTapRef = useRef(0)
  const restoreTimerRef = useRef<number | null>(null)

  // favicon を dev 版へ差し替え + document.title に [DEV] prefix（マウント時一度）
  useEffect(() => {
    const link = document.querySelector("link[rel='icon']") as HTMLLinkElement | null
    const prevHref = link?.getAttribute('href') ?? null
    const prevType = link?.getAttribute('type') ?? null
    if (link) {
      link.setAttribute('type', 'image/svg+xml')
      link.setAttribute('href', '/favicon-dev.svg')
    }
    if (!document.title.startsWith('[DEV] ')) document.title = `[DEV] ${document.title}`
    return () => {
      if (link) {
        if (prevHref !== null) link.setAttribute('href', prevHref)
        if (prevType !== null) link.setAttribute('type', prevType)
      }
    }
  }, [])

  // 画面遷移で自動復活
  useEffect(() => {
    setHidden(false)
    if (restoreTimerRef.current) {
      window.clearTimeout(restoreTimerRef.current)
      restoreTimerRef.current = null
    }
  }, [location.pathname])

  // アンマウント時にタイマー掃除
  useEffect(() => () => {
    if (restoreTimerRef.current) window.clearTimeout(restoreTimerRef.current)
  }, [])

  function hide() {
    setHidden(true)
    if (restoreTimerRef.current) window.clearTimeout(restoreTimerRef.current)
    restoreTimerRef.current = window.setTimeout(() => setHidden(false), AUTO_RESTORE_MS)
  }

  // onClick はタッチ（タップ）・マウス（クリック）両方で発火。
  // 一定間隔内の2回で「隠す」＝ダブルタップ / ダブルクリック統一。
  function handleClick() {
    const now = Date.now()
    if (now - lastTapRef.current < DOUBLE_TAP_MS) {
      lastTapRef.current = 0
      hide()
    } else {
      lastTapRef.current = now
    }
  }

  if (hidden) return null

  return (
    <div
      onClick={handleClick}
      role="status"
      aria-label="開発環境です（ダブルタップ / ダブルクリックで一時的に隠す）"
      title="ダブルタップ / ダブルクリックで一時的に隠す"
      className="w-full h-[36px] bg-[#F59E0B] text-neutral-900 text-xs font-bold tracking-wider
                 flex items-center justify-center whitespace-nowrap select-none cursor-pointer"
      style={{ touchAction: 'manipulation' }}
    >
      DEV ENVIRONMENT
    </div>
  )
}
